/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useCallback } from 'react';
import {
  MdSearch,
  MdAssignmentInd,
  MdContacts,
  MdSecurity,
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';

import { Select, MenuItem, ListItemIcon } from '@material-ui/core';
import { Pagination, Skeleton } from '@material-ui/lab';
import { useDebounce } from 'use-debounce';

import CamundaGroups from '~/components/CamundaGroups';
import Menu from '~/components/Menu';
import Modal from '~/components/Modal';
import PageTitle from '~/components/PageTitle';
import SecurityProfile from '~/components/SecurityProfile';
import Switch from '~/components/Switch';
import Table from '~/components/Table';
import { useAuth } from '~/hooks/auth';
import { IState } from '~/store';
import {
  getUsersRequest,
  activateUserRequest,
  changeUserPasswordRequest,
} from '~/store/modules/users/actions';
import { IUser } from '~/store/modules/users/types';
import { permissions } from '~/types/constants';

import {
  Container,
  HeaderContent,
  Link,
  ContainerInputSearch,
  Content,
  ContentFooter,
  RowData,
} from './styles';

const Users: React.FC = () => {
  const [limit, setLimit] = useState<number>(15);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [editProfileModal, setEditProfileModal] = useState<boolean>(false);
  const [editGroupModal, setEditGroupModal] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [searchDebounce] = useDebounce(search, 800);

  const users = useSelector((state: IState) => state.users.users);
  const pagecount = useSelector((state: IState) => state.users.pagecount);
  const loading = useSelector((state: IState) => state.users.loading);
  const loadingActivation = useSelector(
    (state: IState) => state.users.loadingActivation,
  );

  const { user: loggedUser, checkPermission } = useAuth();

  const dispatch = useDispatch();

  const loadUsers = useCallback(
    (page) => {
      const search = searchDebounce;

      dispatch(getUsersRequest(page, limit, search));
    },
    [searchDebounce, dispatch, limit],
  );

  useEffect(() => {
    const page = 1;

    loadUsers(page);
  }, [searchDebounce, limit, loadUsers]);

  const handleChangePage = useCallback(
    (event, page) => {
      loadUsers(page);
    },
    [loadUsers],
  );

  const handleOpenEditGroupModal = useCallback((user: IUser) => {
    setSelectedUser(user);
    setEditGroupModal(true);
  }, []);

  const handleOpenEditProfileModal = useCallback((user: IUser) => {
    setSelectedUser(user);
    setEditProfileModal(true);
  }, []);

  const handleResetUserPassword = useCallback(
    (user: IUser, managerId?: number) => {
      const confirmOptions = {
        onOk: () =>
          dispatch(
            changeUserPasswordRequest(
              user.id,
              managerId,
              undefined,
              undefined,
              undefined,
              user.dynamicAttributes?.emailContact,
            ),
          ),
        onCancel: () => {},
      };

      toastr.confirm(
        `Tem certeza que deseja resetar a senha de ${user.email}?`,
        confirmOptions,
      );
    },
    [dispatch],
  );

  const handleToggleUserStatus = useCallback(
    (user: IUser) => {
      const activatingOrInactivatingMessage = user.activated
        ? 'Ao inativar o usuário todas suas funções serão removidas.'
        : 'Ao ativar o usuário você poderá atribuir novas funções para que ele possa executar tarefas.';

      const confirmOptions = {
        onOk: () => dispatch(activateUserRequest(user.id, !user.activated)),
        onCancel: () => {},
      };

      toastr.confirm(activatingOrInactivatingMessage, confirmOptions);
    },
    [dispatch],
  );

  return (
    <Container>
      <PageTitle>Usuários</PageTitle>
      <HeaderContent>
        <HeaderContent>
          {checkPermission([permissions.CREATE_USER]) && (
            <Link to="/users/new">Novo Usuário</Link>
          )}
          <ContainerInputSearch>
            <MdSearch size={25} />
            <input
              aria-label="search-input"
              id="search-input"
              type="text"
              placeholder="Buscar usuário"
              onChange={(e) => setSearch(e.target.value)}
            />
          </ContainerInputSearch>
        </HeaderContent>
      </HeaderContent>
      <Content>
        <Table>
          <thead>
            <tr>
              <th />
              <th>Matricula</th>
              <th>Nome</th>
              <th>E-mail</th>
              {checkPermission([permissions.UPDATE_USER]) && <th>Status</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {!loading
              ? users.map((user: IUser) => (
                  // eslint-disable-next-line react/jsx-indent
                  <RowData key={user.id}>
                    <td />
                    <td>{user.id}</td>
                    <td>
                      {user.firstName} {user.lastName}
                    </td>
                    <td>{user.email}</td>

                    {checkPermission([permissions.UPDATE_USER]) && (
                      <td>
                        <Switch
                          disabled={loadingActivation}
                          checked={user.activated || false}
                          handleChange={() => handleToggleUserStatus(user)}
                        >
                          {user.activated ? 'Ativo' : 'Inativo'}
                        </Switch>
                      </td>
                    )}

                    <td>
                      <Menu>
                        {checkPermission([permissions.UPDATE_USER]) &&
                          user.activated && (
                            <MenuItem
                              disabled={loading}
                              onClick={() => handleOpenEditGroupModal(user)}
                            >
                              <ListItemIcon>
                                <MdAssignmentInd size={16} /> Editar funções
                              </ListItemIcon>
                            </MenuItem>
                          )}
                        {checkPermission([permissions.UPDATE_USER]) && (
                          <MenuItem
                            disabled={loading}
                            onClick={() => handleOpenEditProfileModal(user)}
                          >
                            <ListItemIcon>
                              <MdContacts size={16} /> Editar perfil
                            </ListItemIcon>
                          </MenuItem>
                        )}
                        {user.id !== loggedUser.personId &&
                          checkPermission([permissions.UPDATE_USER]) && (
                            <MenuItem
                              disabled={loading}
                              onClick={() =>
                                handleResetUserPassword(
                                  user,
                                  loggedUser.personId,
                                )
                              }
                            >
                              <ListItemIcon>
                                <MdSecurity size={16} /> Resetar senha
                              </ListItemIcon>
                            </MenuItem>
                          )}
                      </Menu>
                    </td>
                  </RowData>
                ))
              : Array.from(Array(limit)).map((value, index) => (
                  // eslint-disable-next-line react/jsx-indent
                  <RowData key={`skeleton-${index}`}>
                    <td />
                    <td>
                      <Skeleton animation="wave" height={40} width="30%" />
                    </td>
                    <td>
                      <Skeleton animation="wave" height={40} width="60%" />
                    </td>
                    <td>
                      <Skeleton animation="wave" height={40} width="60%" />
                    </td>
                    <td>
                      <Skeleton animation="wave" height={40} width="30%" />
                    </td>
                    <td />
                  </RowData>
                ))}
          </tbody>
        </Table>
        <ContentFooter>
          <Pagination
            count={pagecount}
            color="primary"
            onChange={(event, page) => handleChangePage(event, page)}
          />
        </ContentFooter>
      </Content>

      {editGroupModal && selectedUser && (
        <Modal
          title={`Editar função: ${selectedUser.firstName} ${selectedUser.lastName}`}
          subtitle="As alterações só entrarão em vigência no próximo login do usuário"
          open={editGroupModal}
          modalState={(opened) => setEditGroupModal(opened)}
        >
          <CamundaGroups user={selectedUser} />
        </Modal>
      )}

      {editProfileModal && selectedUser && (
        <Modal
          title={`Editar perfil: ${selectedUser.firstName} ${selectedUser.lastName}`}
          subtitle="As alterações só entrarão em vigência no próximo login do usuário"
          open={editProfileModal}
          modalState={(opened) => setEditProfileModal(opened)}
        >
          <SecurityProfile user={selectedUser} />
        </Modal>
      )}
    </Container>
  );
};

export default Users;
